import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { SchoolMapService } from './school-map.service';
import { ThemeCO } from 'src/app/shared/models/ApiModels';

/**
 * Exclude metadata key from CSS variable keys.
 */
type ThemeVariableKey = Exclude<keyof ThemeCO, '_meta'>;

@Injectable({ providedIn: 'root' })
export class ThemeService {

  private themeMap: Record<string, ThemeCO> = {};

  /**
   * Strict allowed keys derived from ThemeCO (excluding _meta).
   * Guarantees compile-time alignment with the interface.
   */
  private readonly THEME_KEYS: readonly ThemeVariableKey[] = [
    'TOP_HEADER_BACKGROUND_COLOR',
    'TOP_HEADER_CODES_COLOR',
    'TOP_HEADER_TEXT_DARK_COLOR',
    'TOP_HEADER_TEXT_WARNING_COLOR',
    'TOP_HEADER_DIVIDER_COLOR',

    'NAVBAR_ACTIVE_LINK_COLOR_DESKTOP',
    'NAVBAR_COLLAPSE_BACKGROUND_COLOR_MOBILE',
    'NAVBAR_LINK_COLOR_MOBILE',
    'NAVBAR_ACTIVE_LINK_COLOR_MOBILE',
    
    'PAY_FEES_BTN_BACKGROUND_COLOR',
    'PAY_FEES_BTN_TEXT_COLOR',
    'PAY_FEES_BTN_BOX_SHADOW',
    'PAY_FEES_BTN_HOVER_BACKGROUND_COLOR',
    'PAY_FEES_BTN_HOVER_TEXT_COLOR',
    'PAY_FEES_BTN_HOVER_TEXT_SHADOW',
    'PAY_FEES_BTN_HOVER_BOX_SHADOW',
    'PAY_FEES_BTN_FOCUS_BOX_SHADOW',

    'ENQUIRY_BTN_TEXT_COLOR',
    'ENQUIRY_BTN_BACKGROUND',
    'ENQUIRY_BTN_BOX_SHADOW',
    'ENQUIRY_BTN_HOVER_BOX_SHADOW',

    'SECTION_HEADING_UNDERLINE_COLOR',
    
    'PPL_BEHIND_VIS_BACKGROUND_COLOR',
    'DIRECTOR_BACKGROUND_COLOR',

    'HEADING_COLOR_1',
    'DIRECTOR_TITLE_COLOR',
    'PRINCIPAL_TITLE_COLOR',

    'ABOUT_US_TEXT_PRIMARY_COLOR',
    'DIRECTOR_TEXT_PRIMARY_COLOR',
    'PRINCIPAL_TEXT_PRIMARY_COLOR',

    'CONTACT_SECTION_BACKGROUND_COLOR',
    'CONTACT_ICON_BACKGROUND_COLOR',
    'CONTACT_ICON_COLOR',
    'CONTACT_INPUT_BORDER_COLOR',
    'CONTACT_INPUT_BACKGROUND_COLOR',
    'CONTACT_INPUT_TEXT_COLOR',
    'CONTACT_SUBMIT_BTN_BACKGROUND_COLOR',
    'CONTACT_SUBMIT_BTN_TEXT_COLOR'
  ] as const;

  constructor(
    private http: HttpClient,
    private schoolMapService: SchoolMapService
  ) {}

  async load(): Promise<void> {
    const rawData = await firstValueFrom(
      this.http.get<Record<string, unknown>>('/assets/theme-config.json')
    );

    if (!rawData || typeof rawData !== 'object') {
      console.error('Invalid theme-config.json structure.');
      return;
    }

    this.themeMap = this.validateThemeMap(rawData);
    this.applyTheme();
  }

  /**
   * Validates each theme entry and builds a safe theme map.
   */
  private validateThemeMap(
    data: Record<string, unknown>
  ): Record<string, ThemeCO> {

    const validated: Record<string, ThemeCO> = {};

    Object.entries(data).forEach(([themeName, value]) => {
      if (!this.isValidTheme(value)) {
        console.warn(`Theme "${themeName}" is invalid and was skipped.`);
        return;
      }

      validated[themeName] = value;
    });

    return validated;
  }

  /**
   * Runtime type guard to ensure required fields exist and are strings.
   * Extra properties are ignored automatically.
   */
  private isValidTheme(obj: unknown): obj is ThemeCO {
    if (!obj || typeof obj !== 'object') return false;

    const theme = obj as Record<string, unknown>;

    return this.THEME_KEYS.every(
      key => typeof theme[key] === 'string'
    );
  }

  private applyTheme(): void {
    const themeName = this.schoolMapService.getTheme();
    if (!themeName) return;

    const theme = this.themeMap[themeName];
    if (!theme) {
      console.warn(`Theme "${themeName}" not found.`);
      return;
    }

    this.applyThemeVariables(theme);
  }

  /**
   * Strict variable application.
   * Only keys defined in ThemeCO (excluding _meta) are applied.
   */
  private applyThemeVariables(theme: ThemeCO): void {
    this.THEME_KEYS.forEach((key) => {
      const value = theme[key]; // strictly inferred as string
      const cssVar = this.toCssVariableName(key);
      if(value) document.documentElement.style.setProperty(cssVar, value || '');
    });
  }

  private toCssVariableName(key: string): string {
    return '--' + key.toLowerCase().replace(/_/g, '-');
  }
}
